import Navigation from '@/components/Navigation';
import FloatingUserMenu from '@/components/FloatingUserMenu';
import Footer from '@/components/Footer';
import SEO from '@/components/SEO';
import { CheckCircle, Eye, Keyboard, Volume2, Mouse } from 'lucide-react';

const Accessibility = () => {
  return (
    <div className="min-h-screen">
      <SEO 
        title="Accessibility Statement | George Dives"
        description="Our commitment to web accessibility and inclusive design"
      />      
      <Navigation />
      <FloatingUserMenu />

      {/* Header */}
      <section className="pt-24 pb-12 bg-gradient-to-br from-blue-600 to-blue-800">
        <div className="container mx-auto px-4 max-w-4xl text-center">
          <h1 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            Accessibility Statement
          </h1>
          <p className="text-lg text-white/90">
            Our commitment to making George Dives accessible to everyone
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="py-12">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="prose prose-lg max-w-none">
            
            {/* Introduction */}
            <div className="bg-white rounded-lg shadow-sm p-8 mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Our Commitment</h2>
              <p className="text-gray-700 mb-4">
                George Dives is committed to ensuring digital accessibility for people with disabilities. 
                We are continually improving the user experience for everyone and applying the relevant 
                accessibility standards.
              </p>
              <p className="text-gray-700">
                We strive to conform to the <strong>Web Content Accessibility Guidelines (WCAG) 2.1</strong> at 
                Level AA. These guidelines explain how to make web content more accessible for people with disabilities 
                and more user-friendly for everyone.
              </p>
            </div>

            {/* Accessibility Features */}
            <div className="bg-white rounded-lg shadow-sm p-8 mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Accessibility Features</h2>
              
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <Eye className="w-6 h-6 text-cyan-600 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Visual Accessibility</h3>
                    <ul className="list-disc list-inside text-gray-700 space-y-1">
                      <li>Sufficient color contrast ratios throughout the site</li>
                      <li>Text that can be resized up to 200% without loss of content or functionality</li>
                      <li>Clear and legible fonts</li>
                      <li>Alternative text for images</li>
                    </ul>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <Keyboard className="w-6 h-6 text-cyan-600 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Keyboard Navigation</h3>
                    <ul className="list-disc list-inside text-gray-700 space-y-1">
                      <li>All functionality available via keyboard</li>
                      <li>Logical tab order throughout pages</li>
                      <li>Visible focus indicators</li>
                      <li>Skip navigation links</li>
                    </ul>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <Volume2 className="w-6 h-6 text-cyan-600 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Screen Reader Compatibility</h3>
                    <ul className="list-disc list-inside text-gray-700 space-y-1">
                      <li>Semantic HTML markup</li>
                      <li>ARIA labels and descriptions where appropriate</li>
                      <li>Clear heading structure</li>
                      <li>Descriptive link text</li>
                    </ul>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <Mouse className="w-6 h-6 text-cyan-600 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Interactive Elements</h3>
                    <ul className="list-disc list-inside text-gray-700 space-y-1">
                      <li>Touch targets sized appropriately for mobile devices</li>
                      <li>Forms with clear labels and error messages</li>
                      <li>Consistent navigation throughout the site</li>
                      <li>No content that flashes more than 3 times per second</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* Known Limitations */}
            <div className="bg-white rounded-lg shadow-sm p-8 mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Known Limitations</h2>
              <p className="text-gray-700 mb-4">
                Despite our best efforts to ensure accessibility, there may be some limitations. Below is a 
                description of known limitations:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2">
                <li>Some third-party embedded content (social media feeds, maps) may not be fully accessible</li>
                <li>Uploaded user-generated images may not have alternative text</li>
                <li>Some PDF documents may not be fully accessible</li>
              </ul>
              <p className="text-gray-700 mt-4">
                We are actively working to address these limitations in future updates.
              </p>
            </div>

            {/* Technical Specifications */}
            <div className="bg-white rounded-lg shadow-sm p-8 mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Technical Specifications</h2>
              <p className="text-gray-700 mb-4">
                This website relies on the following technologies:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2">
                <li>HTML5</li>
                <li>CSS3</li>
                <li>JavaScript (React)</li>
                <li>WAI-ARIA</li>
              </ul>
            </div>

            {/* Feedback */}
            <div className="bg-cyan-50 border border-cyan-200 rounded-lg p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Feedback</h2>
              <p className="text-gray-700 mb-4">
                We welcome your feedback on the accessibility of George Dives. Please let us know if you encounter 
                accessibility barriers:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 mb-4">
                <li><strong>Email:</strong> info@georgedives.com</li>
                <li><strong>Subject Line:</strong> Please include "Accessibility" in the subject</li>
              </ul>
              <p className="text-gray-700">
                We aim to respond to accessibility feedback within <strong>5 business days</strong>.
              </p>
            </div>

            {/* Assessment */}
            <div className="bg-white rounded-lg shadow-sm p-8 mt-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Assessment Approach</h2>
              <p className="text-gray-700 mb-4">
                George Dives assesses the accessibility of this website through:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2">
                <li>Self-evaluation using WCAG 2.1 guidelines</li>
                <li>Automated testing tools</li>
                <li>Manual testing with keyboard navigation</li>
                <li>Screen reader testing</li>
                <li>User feedback</li>
              </ul>
              <p className="text-gray-700 mt-4">
                <strong>Last assessed:</strong> December 2024
              </p>
            </div>

          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Accessibility;